import { createStyles, useBottomInsetSmart, useScaling } from '@/hooks';
import React, {
  cloneElement,
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import {
  BackHandler,
  Platform,
  Pressable,
  Modal as RNModal,
  View,
  type ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  FadeIn,
  FadeOut,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

type SheetModalContextValue = {
  open: boolean;
  setOpen: (next: boolean) => void;
};

const SheetModalContext = createContext<SheetModalContextValue | null>(null);

function useSheetModalContext() {
  const ctx = useContext(SheetModalContext);
  if (!ctx) {
    throw new Error('SheetModal components must be used within <SheetModal>.');
  }
  return ctx;
}

export type SheetModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
};

export const SheetModal: FC<SheetModalProps> = memo(({ open, onOpenChange, children }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? Boolean(open) : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const value = useMemo(() => ({ open: isOpen, setOpen }), [isOpen, setOpen]);

  return <SheetModalContext.Provider value={value}>{children}</SheetModalContext.Provider>;
});

SheetModal.displayName = 'SheetModal';

export type SheetModalTriggerProps = {
  children: ReactNode;
  disabled?: boolean;
  style?: ViewStyle | ViewStyle[];
};

export const SheetModalTrigger: FC<SheetModalTriggerProps> = memo(
  ({ children, disabled = false, style }) => {
    const { setOpen } = useSheetModalContext();
    const handlePress = () => {
      if (disabled) return;
      setOpen(true);
    };

    // If child is a Pressable-like component (e.g. our <Button>), wrapping it
    // with another <Pressable> won't receive presses because events don't bubble.
    // So we clone and inject onPress when possible.
    if (isValidElement(children)) {
      const existingOnPress = (children.props as { onPress?: () => void }).onPress;
      return cloneElement(
        children as React.ReactElement,
        {
          onPress: () => {
            existingOnPress?.();
            handlePress();
          },
        } as never,
      );
    }

    return (
      <Pressable onPress={handlePress} disabled={disabled} style={style} accessibilityRole="button">
        {children}
      </Pressable>
    );
  },
);

SheetModalTrigger.displayName = 'SheetModalTrigger';

export type SheetContentProps = {
  children: ReactNode;
  /**
   * Width of the sheet in landscape (default: min(420, deviceWidth * 0.6)).
   */
  tabletWidth?: number;
  /**
   * Enable Android back button to close (default: true).
   */
  closeOnBackPress?: boolean;
  /**
   * Tap on the backdrop to close (default: false).
   */
  closeOnBackdropPress?: boolean;
  /**
   * Optional style for the sheet container.
   */
  style?: ViewStyle | ViewStyle[];
  /**
   * Enable drag gesture to close the sheet (default: true).
   */
  enableDragToClose?: boolean;
};

export const SheetContent: FC<SheetContentProps> = memo(
  ({
    children,
    tabletWidth,
    closeOnBackPress = true,
    closeOnBackdropPress = false,
    style,
    enableDragToClose = true,
  }) => {
    const { open, setOpen } = useSheetModalContext();
    const styles = useStyles();
    const { deviceWidth, deviceHeight, isTablet, scale } = useScaling();
    const [mounted, setMounted] = useState(open);

    const bottomInset = useBottomInsetSmart();

    // Translate values are relative to "open" position (0).
    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);

    const sheetWidth = useMemo(() => {
      const fallback = Math.min(deviceWidth * 0.6, 420);
      return tabletWidth ?? fallback;
    }, [deviceWidth, tabletWidth]);

    const close = useCallback(() => setOpen(false), [setOpen]);

    useEffect(() => {
      if (open) {
        setMounted(true);
      }
    }, [open]);

    const animateOpen = useCallback(() => {
      if (isTablet) {
        translateX.value = sheetWidth;
        translateY.value = 0;
        translateX.value = withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) });
      } else {
        translateY.value = deviceHeight;
        translateX.value = 0;
        translateY.value = withTiming(0, { duration: 220, easing: Easing.out(Easing.cubic) });
      }
    }, [deviceHeight, isTablet, sheetWidth, translateX, translateY]);

    const animateClose = useCallback(() => {
      const onEnd = () => {
        runOnJS(setMounted)(false);
      };
      if (isTablet) {
        translateX.value = withTiming(
          sheetWidth,
          { duration: 180, easing: Easing.in(Easing.cubic) },
          onEnd,
        );
      } else {
        translateY.value = withTiming(
          deviceHeight,
          { duration: 180, easing: Easing.in(Easing.cubic) },
          onEnd,
        );
      }
    }, [deviceHeight, isTablet, sheetWidth, translateX, translateY]);

    useEffect(() => {
      if (!mounted) return;
      if (open) animateOpen();
      else animateClose();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mounted]);

    useEffect(() => {
      if (!closeOnBackPress) return;
      const sub = BackHandler.addEventListener('hardwareBackPress', () => {
        if (!mounted || !open) return false;
        close();
        return true;
      });
      return () => sub.remove();
    }, [close, closeOnBackPress, mounted, open]);

    const panGesture = useMemo(() => {
      const DRAG_CLOSE_THRESHOLD = 80;
      const VELOCITY_CLOSE_THRESHOLD = 1200;
      if (isTablet) {
        return Gesture.Pan()
          .onUpdate(e => {
            translateX.value = Math.max(0, e.translationX);
          })
          .onEnd(e => {
            const shouldClose =
              e.translationX > DRAG_CLOSE_THRESHOLD || e.velocityX > VELOCITY_CLOSE_THRESHOLD;
            if (shouldClose) {
              runOnJS(close)();
              return;
            }
            translateX.value = withTiming(0, { duration: 160, easing: Easing.out(Easing.cubic) });
          });
      }
      return Gesture.Pan()
        .onUpdate(e => {
          translateY.value = Math.max(0, e.translationY);
        })
        .onEnd(e => {
          const shouldClose =
            e.translationY > DRAG_CLOSE_THRESHOLD || e.velocityY > VELOCITY_CLOSE_THRESHOLD;
          if (shouldClose) {
            runOnJS(close)();
            return;
          }
          translateY.value = withTiming(0, { duration: 160, easing: Easing.out(Easing.cubic) });
        });
    }, [close, isTablet, translateX, translateY]);

    const sheetAnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
    }));

    if (!mounted) return null;

    return (
      <RNModal
        transparent
        visible={mounted}
        animationType="none"
        onRequestClose={closeOnBackPress ? close : undefined}
        supportedOrientations={['portrait', 'landscape']}
        statusBarTranslucent
        navigationBarTranslucent={Platform.OS === 'android'}>
        {/* Required for Android gestures inside RN Modal */}
        <GestureHandlerRootView style={styles.modalRoot}>
          <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.backdrop} />

          <Pressable
            style={styles.backdropOverlay}
            onPress={closeOnBackdropPress ? close : undefined}
          />

          <View style={styles.container}>
            {enableDragToClose ? (
              <GestureDetector gesture={panGesture}>
                <Animated.View
                  style={[
                    isTablet
                      ? [styles.sheetLandscape, { width: sheetWidth }]
                      : styles.sheetPortrait,
                    sheetAnimatedStyle,
                    {
                      paddingBottom:
                        bottomInset +
                        (Platform.OS === 'android' ? scale(16) : scale(0)) +
                        (Platform.OS === 'web' ? 16 : 0),
                    },
                    style,
                  ]}>
                  {children}
                </Animated.View>
              </GestureDetector>
            ) : (
              <Animated.View
                style={[
                  isTablet ? [styles.sheetLandscape, { width: sheetWidth }] : styles.sheetPortrait,
                  sheetAnimatedStyle,
                  {
                    paddingBottom:
                      bottomInset +
                      (Platform.OS === 'android' ? scale(16) : scale(0)) +
                      (Platform.OS === 'web' ? 16 : 0),
                  },
                  style,
                ]}>
                {children}
              </Animated.View>
            )}
          </View>
        </GestureHandlerRootView>
      </RNModal>
    );
  },
);

SheetContent.displayName = 'SheetContent';

const useStyles = createStyles(({ scale }) => ({
  modalRoot: {
    flex: 1,
  },
  // Stretch to fill whatever area the RN Modal renders into. Hardcoding `width/height` from
  // `useScaling()` breaks on Samsung DeX / freeform Android windows where the modal's actual
  // surface can differ from the app window dimensions.
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  backdropOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  sheetPortrait: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    // Percentage cap follows the actual parent height (DeX-safe) instead of a stale `deviceHeight`.
    maxHeight: '100%',
    backgroundColor: 'white',
    borderTopLeftRadius: scale(40),
    borderTopRightRadius: scale(40),
    overflow: 'hidden',
  },
  // Stretch top→bottom so landscape sheet fills the modal area regardless of window size.
  sheetLandscape: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'white',
    overflow: 'hidden',
  },
}));
